import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { BRAND_NAME_MAX_CHARS, BrandUpdateSchema, deriveBrandColors } from '@tallyframe/core';
import { ReportHeader } from '@tallyframe/renderer';
import { useDeleteLogo, useUpdateBrand, useUploadLogo } from '../../api/settings-queries';
import { useSession } from '../../app/session-context';
import { useBrand } from '../../brandContext';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { DropZone } from '../../ui/DropZone';
import { Icon } from '../../ui/Icon';
import { InputField } from '../../ui/Input';
import { Stack } from '../../ui/Stack';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg';
const PREVIEW_TITLE = 'Weekly Sales Review';
const SWATCH_CLASS = 'inline-block w-4 h-4 rounded-control border border-solid border-line';
const SWATCH_GROUP_CLASS = 'inline-flex items-center gap-2 text-sm text-muted';

/**
 * The brand section of settings: a display name, a logo and a brand color,
 * plus a live preview built from the same `ReportHeader` every report uses.
 *
 * The color field is a raw text draft, not the validated accent: a controlled
 * input bound straight to the accent would erase whatever is mid-typed as it
 * passes through an invalid intermediate state ("#", "#0"). Only a complete
 * `#RRGGBB` feeds `deriveBrandColors`, the preview and Save.
 *
 * The draft is seeded from the brand in an effect, not the `useState`
 * initializer: the brand loads after the first render, so a one-time
 * initializer would leave the form blank with Save ready to overwrite the
 * saved brand. `seededRef` makes the seed one-shot, so a later delivery (this
 * page's own save) never clobbers an edit in progress; `dirtyRef`, set from
 * both fields' change handlers, blocks the seed once the operator has typed
 * anything, even before the brand arrived.
 */
export function BrandSettings(): ReactElement {
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const { brand } = useBrand();
  const update = useUpdateBrand(workspaceId);
  const upload = useUploadLogo(workspaceId);
  const remove = useDeleteLogo(workspaceId);

  const [displayName, setDisplayName] = useState(brand?.displayName ?? '');
  const [colorText, setColorText] = useState(brand?.accent ?? '');
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [logoError, setLogoError] = useState<unknown>(null);

  const seededRef = useRef(false);
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (brand && !seededRef.current && !dirtyRef.current) {
      seededRef.current = true;
      setDisplayName(brand.displayName);
      setColorText(brand.accent ?? '');
    }
  }, [brand]);

  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    },
    []
  );

  const trimmedAccent = colorText.trim();
  const isEmptyAccent = trimmedAccent.length === 0;
  const isValidAccent = HEX_COLOR.test(trimmedAccent);
  const saving = update.isPending;
  const canSave = !saving && (isEmptyAccent || isValidAccent);
  const derived = isValidAccent ? deriveBrandColors(trimmedAccent) : null;
  const darkened = derived !== null && derived.text.toLowerCase() !== trimmedAccent.toLowerCase();
  // The hint is the input's description, so it carries the derived hex too.
  const colorHint = darkened ? `Darkened slightly so text stays readable. Text color ${derived.text}.` : undefined;
  const logoBusy = upload.isPending || remove.isPending;

  const save = () => {
    setSaveError(null);
    setJustSaved(false);
    let body;
    try {
      body = BrandUpdateSchema.parse({ displayName, accent: isValidAccent ? trimmedAccent : null });
    } catch (cause) {
      setSaveError(cause);
      return;
    }
    update.mutate(body, {
      onSuccess: () => {
        setJustSaved(true);
        if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = window.setTimeout(() => setJustSaved(false), 1600);
      },
      onError: (cause) => setSaveError(cause)
    });
  };

  const uploadLogo = (file: File) => {
    setLogoError(null);
    upload.mutate(file, { onError: (cause) => setLogoError(cause) });
  };

  const removeLogo = () => {
    setLogoError(null);
    remove.mutate(undefined, { onError: (cause) => setLogoError(cause) });
  };

  const previewBrand = {
    name: displayName,
    accent: isValidAccent ? trimmedAccent : null,
    logoUrl: brand?.logoUrl ?? null
  };

  return (
    <Stack gap={6}>
      <Card title="Brand">
        <Stack gap={4}>
          <InputField
            label="Display name"
            value={displayName}
            maxLength={BRAND_NAME_MAX_CHARS}
            onChange={(value) => {
              dirtyRef.current = true;
              setDisplayName(value);
            }}
          />

          <Stack gap={2}>
            {brand?.logoUrl ? (
              <div className="flex items-center gap-3">
                <img className="h-8" src={brand.logoUrl} alt={`${brand.displayName || 'Operator'} logo`} />
                <Button variant="quiet" size="sm" onPress={removeLogo} isDisabled={logoBusy}>
                  <Icon name="close" size={14} />
                  Remove
                </Button>
              </div>
            ) : null}
            <DropZone
              accept={LOGO_ACCEPT}
              label="Logo"
              onFile={uploadLogo}
              onReject={() => setLogoError(new Error('Use a PNG, JPEG or SVG file'))}
              disabled={logoBusy}
            />
            <ErrorBanner error={logoError} />
          </Stack>

          <Stack gap={2}>
            <InputField
              label="Brand color"
              value={colorText}
              hint={colorHint}
              onChange={(value) => {
                dirtyRef.current = true;
                setColorText(value);
              }}
            />
            {isValidAccent ? (
              <div className="flex items-center gap-4">
                <span className={SWATCH_GROUP_CLASS}>
                  <span className={SWATCH_CLASS} style={{ background: trimmedAccent }} />
                  Your color
                </span>
                {darkened ? (
                  <span className={SWATCH_GROUP_CLASS}>
                    <span className={SWATCH_CLASS} style={{ background: derived.text }} />
                    Used for text
                  </span>
                ) : null}
              </div>
            ) : null}
          </Stack>

          <ErrorBanner error={saveError} />

          <div>
            <Button variant="primary" onPress={save} isDisabled={!canSave}>
              {saving ? 'Saving…' : justSaved ? 'Saved' : 'Save'}
            </Button>
          </div>
        </Stack>
      </Card>

      <Card title="Preview">
        <ReportHeader title={PREVIEW_TITLE} preparedFor="Your client" brand={previewBrand} />
      </Card>
    </Stack>
  );
}
